import { useState, useEffect, useCallback, useContext, useMemo } from "react";
import Header from "@/components/Header";
import {
  Bell,
  Phone,
  MessageCircle,
  Video,
  Calendar,
  CreditCard,
  AlertCircle,
  Star,
  Package,
  Info,
  Check,
} from "lucide-react";
import { cn } from "@/utils/cn";
import { format, formatDistanceToNow } from "date-fns";

import { AuthContext } from "@/context/Auth";
import {
  getAstrologerNotifications,
  markAllAstrologerNotificationsRead,
  markAstrologerNotificationRead,
} from "@/api/notifications";

/**
 * Map the server's icon enum to a lucide-react component. Icons stay
 * in sync with the server's NOTIFICATION_ICONS enum
 * (server/src/models/common/notification.model.js). Falls back to
 * Info if the server sends something unrecognised.
 */
const ICON_MAP = {
  calendar: Calendar,
  phone: Phone,
  video: Video,
  "message-circle": MessageCircle,
  "credit-card": CreditCard,
  gift: Package,
  package: Package,
  "alert-circle": AlertCircle,
  star: Star,
  info: Info,
};

/**
 * Type → tile-tone mapping for the rounded icon background. Driven by
 * the notification's `type` field (astrologer_* prefixes) so the
 * server controls the tone, not the client.
 */
const typeTone = (type) => {
  if (!type) return "bg-gray-100 text-gray-600";
  if (type.includes("appointment_booked")) return "bg-blue-100 text-blue-600";
  if (type.includes("instant_call")) return "bg-orange-100 text-orange-600";
  if (type.includes("review")) return "bg-yellow-100 text-yellow-600";
  if (type.includes("payout")) return "bg-green-100 text-green-600";
  if (type.includes("cancel")) return "bg-red-100 text-red-600";
  if (type.includes("reschedul")) return "bg-purple-100 text-purple-600";
  return "bg-gray-100 text-gray-600";
};

const formatTimeAgo = (date) => {
  if (!date) return "";
  try {
    const d = typeof date === "string" ? new Date(date) : date;
    const ms = Date.now() - d.getTime();
    // For very recent rows, formatDistanceToNow returns "less than a
    // minute" which reads as filler — show "just now" instead.
    if (ms < 60_000) return "just now";
    if (ms < 7 * 24 * 60 * 60 * 1000) {
      return formatDistanceToNow(d, { addSuffix: true });
    }
    return format(d, "MMM d");
  } catch {
    return "";
  }
};

const NotificationItem = ({ notification, onMarkRead }) => {
  const Icon = ICON_MAP[notification.icon] || Info;
  const isUnread = !notification.readAt;

  return (
    <div
      className={cn(
        "flex items-start gap-3 p-4 border-b hover:bg-gray-50 transition-colors",
        isUnread && "bg-orange-50/50"
      )}
    >
      <div
        className={cn(
          "w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0",
          typeTone(notification.type)
        )}
      >
        <Icon className="h-5 w-5" />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p
              className={cn(
                "text-sm",
                isUnread
                  ? "font-semibold text-gray-900"
                  : "font-medium text-gray-700"
              )}
            >
              {notification.title}
            </p>
            <p className="text-sm text-gray-500 mt-0.5">{notification.body}</p>
          </div>
          <span className="text-xs text-gray-400 whitespace-nowrap">
            {formatTimeAgo(notification.createdAt)}
          </span>
        </div>

        {isUnread && (
          <div className="flex items-center gap-2 mt-2">
            <button
              onClick={() => onMarkRead(notification._id)}
              className="flex items-center gap-1 text-xs text-orange-500 hover:text-orange-600"
            >
              <Check className="h-3 w-3" />
              Mark read
            </button>
          </div>
        )}
      </div>

      {isUnread && (
        <div className="w-2 h-2 rounded-full bg-orange-500 flex-shrink-0 mt-2" />
      )}
    </div>
  );
};

export default function NotificationsPage() {
  const { astrologer } = useContext(AuthContext);
  const astrologerId = astrologer?.astrologerId || null;

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [filter, setFilter] = useState("all"); // "all" | "unread"
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!astrologerId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const { status, data, error: errMsg } = await getAstrologerNotifications(
      astrologerId,
      { limit: 50 }
    );
    if (status >= 200 && status < 300) {
      setNotifications(Array.isArray(data?.items) ? data.items : []);
      setUnreadCount(Number(data?.unreadCount) || 0);
    } else {
      setError(errMsg || "Failed to load notifications");
    }
    setLoading(false);
  }, [astrologerId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleMarkRead = useCallback(
    async (id) => {
      if (!astrologerId || !id) return;
      // Optimistic — flip in place, decrement count. Server is the
      // source of truth so on failure we re-fetch.
      setNotifications((prev) =>
        prev.map((n) =>
          n._id === id ? { ...n, readAt: new Date().toISOString() } : n
        )
      );
      setUnreadCount((c) => Math.max(0, c - 1));
      const { status } = await markAstrologerNotificationRead(astrologerId, id);
      if (status < 200 || status >= 300) {
        // Roll back by re-fetching the truth.
        load();
      }
    },
    [astrologerId, load]
  );

  const handleMarkAllRead = useCallback(async () => {
    if (!astrologerId) return;
    const now = new Date().toISOString();
    setNotifications((prev) =>
      prev.map((n) => (n.readAt ? n : { ...n, readAt: now }))
    );
    setUnreadCount(0);
    const { status } = await markAllAstrologerNotificationsRead(astrologerId);
    if (status < 200 || status >= 300) {
      load();
    }
  }, [astrologerId, load]);

  const visible = useMemo(() => {
    if (filter === "unread") return notifications.filter((n) => !n.readAt);
    return notifications;
  }, [filter, notifications]);

  return (
    <>
      <Header name="Notifications" />
      <div className="w-full p-4 max-w-3xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm">
          {/* Header tabs */}
          <div className="p-4 border-b flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setFilter("all")}
                className={cn(
                  "text-sm font-medium pb-1 border-b-2 transition-colors",
                  filter === "all"
                    ? "border-orange-500 text-orange-500"
                    : "border-transparent text-gray-500"
                )}
              >
                All
              </button>
              <button
                onClick={() => setFilter("unread")}
                className={cn(
                  "text-sm font-medium pb-1 border-b-2 transition-colors flex items-center gap-1",
                  filter === "unread"
                    ? "border-orange-500 text-orange-500"
                    : "border-transparent text-gray-500"
                )}
              >
                Unread
                {unreadCount > 0 && (
                  <span className="px-1.5 py-0.5 text-xs bg-orange-500 text-white rounded-full">
                    {unreadCount}
                  </span>
                )}
              </button>
            </div>

            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  className="text-xs text-orange-500 hover:text-orange-600"
                >
                  Mark all read
                </button>
              )}
            </div>
          </div>

          {/* List */}
          {loading ? (
            <div className="text-center py-12 text-gray-500">
              <p className="text-sm">Loading notifications...</p>
            </div>
          ) : error ? (
            <div className="text-center py-12 text-gray-500">
              <AlertCircle className="h-12 w-12 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">{error}</p>
              <button
                onClick={load}
                className="mt-3 text-xs text-orange-500 hover:text-orange-600"
              >
                Try again
              </button>
            </div>
          ) : visible.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Bell className="h-12 w-12 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">
                {filter === "unread"
                  ? "No unread notifications"
                  : "No notifications yet"}
              </p>
            </div>
          ) : (
            <div>
              {visible.map((n) => (
                <NotificationItem
                  key={n._id}
                  notification={n}
                  onMarkRead={handleMarkRead}
                />
              ))}
            </div>
          )}

          <div className="p-4 bg-gray-50 border-t">
            <p className="text-xs text-gray-500 text-center">
              Notifications are stored for 30 days
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
